import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthShell, { fieldClass } from '../components/AuthShell.js';
import { Button } from '../components/ui.js';
import { useT } from '../lib/i18n.js';
import { apiFetch } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';

const MIN = 10;

/**
 * Replacing the password an administrator handed over.
 *
 * Reached by being refused everywhere else: while the account is on a
 * temporary password the server allows it nothing but this. That is on
 * purpose — the person who wrote the password on a slip of paper can read it
 * back for as long as it works.
 *
 * Changing it drops every session, including this one, so it ends at the
 * sign-in screen rather than pretending otherwise.
 */
export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  const { t } = useT();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (next !== confirm) {
      setError('Those two do not match');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await apiFetch('/api/auth/change-password', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
      });
      clear();
      navigate('/signin', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change your password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title={t('Choose your own password')}
      subtitle={t('You are signed in with a password somebody gave you. Replace it before carrying on.')}
    >
      <form
        className="flex flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label
          htmlFor="current"
          className="mb-2 block text-[13px] font-extrabold text-muted md:text-[14.5px]"
        >
          {t('The password you were given')}
        </label>
        <input
          id="current"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className={fieldClass}
        />

        <label
          htmlFor="next"
          className="mb-2 block text-[13px] font-extrabold text-muted md:text-[14.5px]"
        >
          Your own, {MIN} characters or more
        </label>
        <input
          id="next"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          className={fieldClass}
        />

        <label
          htmlFor="confirm"
          className="mb-2 block text-[13px] font-extrabold text-muted md:text-[14.5px]"
        >
          {t('Again')}
        </label>
        <input
          id="confirm"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className={fieldClass}
        />

        <Button
          type="submit"
          disabled={busy || current.length === 0 || next.length < MIN || confirm.length === 0}
        >
          {busy ? `${t('Save and sign in again')}…` : t('Save and sign in again')}
        </Button>

        {error && (
          <p className="mt-3.5 rounded-tile bg-danger-soft px-4 py-3.5 text-[13.5px] font-bold text-danger md:text-[15px]">
            {error}
          </p>
        )}
      </form>
    </AuthShell>
  );
}
